import { Skeleton } from './skeleton';

export interface SkeletonListProps {
  /** Number of list rows to render (default: 3) */
  rows?: number;
  className?: string;
  /** Overrides the sr-only text announced once while this skeleton is shown. */
  labels?: { loading?: string };
}

/**
 * Content-shaped skeleton for list-style widgets.
 * Each row renders an avatar circle + two text bars.
 * Animation delays are staggered 0.12s per row so the shimmer reads as alive.
 *
 * The rows themselves stay `aria-hidden` (they're decorative shimmer bars),
 * but the surrounding `role="status"` region announces the loading state
 * once — screen reader users otherwise get no indication content is loading
 * at all (WCAG 4.1.2).
 */
export function SkeletonList({
  rows = 3,
  className,
  labels,
}: SkeletonListProps) {
  return (
    <div role="status">
      <span className="sr-only">{labels?.loading ?? 'Loading…'}</span>
      <div
        className={className}
        aria-hidden="true"
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        {Array.from({ length: rows }, (_, i) => (
          <div
            key={i}
            style={{ display: 'flex', alignItems: 'center', gap: 10 }}
          >
            <Skeleton
              variant="circle"
              width={36}
              height={36}
              style={{
                animationDelay: `${(i * 0.12).toString()}s`,
                flexShrink: 0,
              }}
            />
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <Skeleton
                variant="text"
                width="70%"
                height={13}
                style={{ animationDelay: `${(i * 0.12).toString()}s` }}
              />
              <Skeleton
                variant="text"
                width="45%"
                height={11}
                style={{ animationDelay: `${(i * 0.12 + 0.06).toString()}s` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
